<template>
  <div class="registration-page" :class="{'dark-mode': darkMode}">
    <!-- Theme Toggle -->
    <div class="theme-toggle">
      <button @click="toggleTheme" class="theme-btn" :aria-label="darkMode ? 'Switch to light mode' : 'Switch to dark mode'">
        <i class="fas" :class="darkMode ? 'fa-sun' : 'fa-moon'"></i>
      </button>
    </div>

    <!-- Animated Background -->
    <div class="animated-bg"></div>
    
    <!-- Main Content -->
    <div class="container main-content">
      <div class="row justify-content-center">
        <div class="col-12 col-md-8 col-lg-6 col-xl-5">
          <!-- Registration Card with subtle animations -->
          <transition name="card-slide" appear>
            <div class="card registration-card">
              <div class="card-body p-4 p-md-5">
                <!-- Logo with animation -->
                <transition name="fade" appear>
                  <div class="logo-container mb-4">
                    <img src="../assets/logo.png" alt="LMS Logo" class="logo">
                  </div>
                </transition>
                
                <h3 class="text-center mb-2">Create Account</h3>
                <p class="text-center text-muted mb-4">Join us to start your learning journey</p>
                
                <!-- Registration Form -->
                <form @submit.prevent="userregister" novalidate>
                  <!-- Name Input -->
                  <div class="form-floating mb-3">
                    <input 
                      type="text" 
                      id="fname" 
                      v-model.trim="register.name"
                      class="form-control" 
                      :class="{'is-invalid': errors.name}"
                      placeholder="Full Name"
                      required
                      @blur="validateName"
                    >
                    <label for="fname">Full Name</label>
                    <div v-if="errors.name" class="invalid-feedback">
                      {{ errors.name }}
                    </div>
                  </div>
                  
                  <!-- Mobile Input -->
                  <div class="form-floating mb-3">
                    <input 
                      type="tel" 
                      id="mobile" 
                      v-model.trim="register.mobile"
                      class="form-control" 
                      :class="{'is-invalid': errors.mobile}"
                      placeholder="Mobile Number"
                      required
                      @blur="validateMobile"
                      aria-describedby="mobileHelp"
                    >
                    <label for="mobile">Mobile Number</label>
                    <div v-if="errors.mobile" class="invalid-feedback">
                      {{ errors.mobile }}
                    </div>
                    <div id="mobileHelp" class="form-text">We'll never share your number with anyone else.</div>
                  </div>
                  
                  <!-- Password Input -->
                  <div class="form-floating mb-4">
                    <input 
                      :type="showPassword ? 'text' : 'password'" 
                      id="password" 
                      v-model.trim="register.password"
                      class="form-control" 
                      :class="{'is-invalid': errors.password}"
                      placeholder="Password"
                      required
                      maxlength="10"
                      @blur="validatePassword"
                    >
                    <label for="password">Password</label>
                    <button 
                      type="button" 
                      class="btn btn-sm password-toggle"
                      @click="showPassword = !showPassword"
                      :aria-label="showPassword ? 'Hide password' : 'Show password'"
                    >
                      <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                    </button>
                    <div v-if="errors.password" class="invalid-feedback">
                      {{ errors.password }}
                    </div>
                    <div class="form-text">Must be at least 6 characters</div>
                  </div>
                  
                  <!-- Terms Agreement -->
                  <div class="form-check mb-4">
                    <input 
                      type="checkbox" 
                      class="form-check-input" 
                      id="termsAgreement"
                      v-model="termsAgreed"
                      :class="{'is-invalid': errors.terms}"
                    >
                    <label class="form-check-label" for="termsAgreement">
                      I agree to the <a href="#" class="text-decoration-none">Terms of Service</a> and <a href="#" class="text-decoration-none">Privacy Policy</a>
                    </label>
                    <div v-if="errors.terms" class="invalid-feedback">
                      {{ errors.terms }}
                    </div>
                  </div>
                  
                  <!-- Submit Button -->
                  <button 
                    type="submit" 
                    class="btn btn-primary w-100 py-3 mb-3"
                    :disabled="loading"
                  >
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    {{ loading ? 'Processing...' : 'Create Account' }}
                  </button>
                </form>
                
                <!-- Divider -->
                <!--<div class="divider mb-4">-->
                <!--  <span>Or sign up with</span>-->
                <!--</div>-->
                
                <!-- Social Registration -->
                <!--<div class="d-grid gap-2 social-register mb-4">-->
                <!--  <button type="button" class="btn btn-outline-secondary">-->
                <!--    <i class="fab fa-google me-2"></i> Google-->
                <!--  </button>-->
                <!--  <button type="button" class="btn btn-outline-secondary">-->
                <!--    <i class="fab fa-facebook me-2"></i> Facebook-->
                <!--  </button>-->
                <!--</div>-->
                
                <!-- Login link -->
                <!--<div class="text-center">-->
                <!--  <p class="mb-0">Already have an account? -->
                <!--    <router-link :to="'/'" class="text-decoration-none fw-semibold">Login here</router-link>-->
                <!--  </p>-->
                <!--</div>-->
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
    
    <!-- OTP Verification Modal -->
    <div v-if="verified" class="modal-backdrop show"></div>
    <transition name="modal-fade">
      <div v-if="verified" class="modal show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Verify OTP</h5>
              <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <p>We've sent a verification code to your mobile number</p>
              <div class="mb-3">
                <label for="otp" class="form-label">Enter OTP</label>
                <input 
                  type="text" 
                  id="otp" 
                  v-model.trim="verify.otp"
                  class="form-control" 
                  placeholder="Enter 6-digit code"
                  maxlength="6"
                >
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <p class="mb-0 small">Didn't receive the code?</p>
                <button type="button" class="btn btn-link p-0" @click="resendOTP">Resend OTP</button>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
              <button type="button" class="btn btn-primary" @click="verifyotp" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                Verify & Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
    
    <!-- Footer -->
    <footer class="footer mt-auto">
      <div class="container">
        <div class="d-flex align-items-center justify-content-center py-3">
          <p class="mb-0 small">&copy; {{ new Date().getFullYear() }} LMS Platform. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.registration-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding-top: 2rem;
}

.dark-mode {
  background: linear-gradient(135deg, #232526 0%, #414345 100%);
  color: #f8f9fa;
}

/* Animated Background */
.animated-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40vh;
  background: linear-gradient(57deg, #6027be, #000327);
  border-radius: 0 0 50% 50%/ 0 0 100% 100%;
  transform: scaleX(1.5);
  z-index: 0;
  animation: gradientShift 15s ease infinite;
}

.dark-mode .animated-bg {
  background: linear-gradient(57deg, #2d1b69, #0a0826);
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Theme Toggle */
.theme-toggle {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 10;
}

.theme-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  transition: all 0.3s ease;
}

.dark-mode .theme-btn {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.theme-btn:hover {
  transform: rotate(15deg);
  background: rgba(255, 255, 255, 0.3);
}

/* Main Content */
.main-content {
  position: relative;
  z-index: 1;
  padding-top: 2rem;
  padding-bottom: 2rem;
}

/* Registration Card */
.registration-card {
  border: none;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.85);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.dark-mode .registration-card {
  background: rgba(30, 30, 40, 0.85);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  color: #f8f9fa;
}

.registration-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
}

.dark-mode .registration-card:hover {
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4);
}

/* Logo */
.logo-container {
  text-align: center;
}

.logo {
  max-width: 180px;
  height: auto;
  transition: all 0.5s ease;
}

.logo:hover {
  transform: scale(1.05);
}

/* Form Elements */
.form-floating {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #6c757d;
  z-index: 5;
}

.dark-mode .form-control {
  background-color: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.15);
  color: #f8f9fa;
}

.dark-mode .form-control:focus {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: var(--primary);
  color: #f8f9fa;
  box-shadow: 0 0 0 0.25rem rgba(99, 102, 241, 0.25);
}

.dark-mode .form-control::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.dark-mode .form-check-input {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.dark-mode .form-text {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* Divider */
.divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #6c757d;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #dee2e6;
}

.divider::before {
  margin-right: 0.5em;
}

.divider::after {
  margin-left: 0.5em;
}

.dark-mode .divider {
  color: rgba(255, 255, 255, 0.6);
}

.dark-mode .divider::before,
.dark-mode .divider::after {
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

/* Modal Styles */
.modal-backdrop {
  opacity: 0.5;
  z-index: 1040;
}

.modal {
  z-index: 1050;
}

.modal-content {
  border: none;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.dark-mode .modal-content {
  background-color: #1f2937;
  color: #f8f9fa;
}

/* Footer */
.footer {
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.dark-mode .footer {
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}

/* Animations */
.card-slide-enter-active {
  animation: slideInUp 0.6s ease;
}

.fade-enter-active {
  animation: fadeIn 0.8s ease 0.3s both;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .registration-page {
    padding-top: 1rem;
  }
  
  .animated-bg {
    height: 30vh;
  }
  
  .theme-toggle {
    top: 1rem;
    right: 1rem;
  }
  
  .theme-btn {
    width: 40px;
    height: 40px;
  }
  
  .modal-dialog {
    margin: 1rem;
  }
}
</style>

<script>
import axios from "@/plugins/axios";
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';

export default {
  name: 'RegistrationView',
  setup() {
    const router = useRouter();
    const darkMode = ref(false);
    const loading = ref(false);
    const showPassword = ref(false);
    const termsAgreed = ref(false);
    const verified = ref(false);
    const otpverified = ref(false);
    
    const register = ref({
      name: '',
      mobile: '',
      password: '',
      language: 'English',
    });
    
    const verify = ref({
      otp: '',
      mobile: ''
    });
    
    const errors = ref({
      name: '',
      mobile: '',
      password: '',
      terms: ''
    });

    // Check for saved theme preference or respect OS preference
    onMounted(() => {
      document.getElementById('global-loader').style.display = 'none';
      const token = localStorage?.getItem("token");
      if (token) {
        router.replace('/dashboard');
      }
      
      // Check for saved theme preference
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        darkMode.value = savedTheme === 'dark';
      } else {
        // Respect OS preference if no saved preference
        darkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      applyTheme();
    });

    const toggleTheme = () => {
      darkMode.value = !darkMode.value;
      localStorage.setItem('theme', darkMode.value ? 'dark' : 'light');
      applyTheme();
    };

    const applyTheme = () => {
      if (darkMode.value) {
        document.documentElement.setAttribute('data-bs-theme', 'dark');
      } else {
        document.documentElement.setAttribute('data-bs-theme', 'light');
      }
    };

    const validateName = () => {
      if (!register.value.name) {
        errors.value.name = 'Full name is required';
        return false;
      }
      
      if (register.value.name.length < 2) {
        errors.value.name = 'Please enter a valid name';
        return false;
      }
      
      errors.value.name = '';
      return true;
    };

    const validateMobile = () => {
      if (!register.value.mobile) {
        errors.value.mobile = 'Mobile number is required';
        return false;
      }
      
      const mobileRegex = /^[0-9]{10}$/;
      if (!mobileRegex.test(register.value.mobile)) {
        errors.value.mobile = 'Please enter a valid 10-digit mobile number';
        return false;
      }
      
      errors.value.mobile = '';
      return true;
    };

    const validatePassword = () => {
      if (!register.value.password) {
        errors.value.password = 'Password is required';
        return false;
      }
      
      if (register.value.password.length < 6) {
        errors.value.password = 'Password must be at least 6 characters';
        return false;
      }
      
      errors.value.password = '';
      return true;
    };

    const validateTerms = () => {
      if (!termsAgreed.value) {
        errors.value.terms = 'You must agree to the terms and conditions';
        return false;
      }
      
      errors.value.terms = '';
      return true;
    };

    const userregister = async () => {
      // Validate form
      const isNameValid = validateName();
      const isMobileValid = validateMobile();
      const isPasswordValid = validatePassword();
      const isTermsValid = validateTerms();
      
      if (!isNameValid || !isMobileValid || !isPasswordValid || !isTermsValid) return;
      
      loading.value = true;
      
      try {
        const response = await axios.post("userregister", register.value);
        
        if (response.data.success == 2) { 
          router.push('/');
        }
        
        // showNotification(response.data.message, response.data.success ? 'success' : 'error');
        
        if (response.data.success) {
          verify.value.mobile = register.value.mobile;
          verified.value = true;
          otpverified.value = true;
        }
      } catch (err) {
        showNotification('Registration failed. Please try again.', 'error');
        console.error('Registration error:', err);
      } finally {
        loading.value = false;
      }
    };

    const verifyotp = async () => {
      if (!verify.value.otp) {
        showNotification('Please enter the OTP', 'error');
        return;
      }
      
      loading.value = true;
      
      try {
        const response = await axios.post("verifyotp", verify.value);
        
        // showNotification(response.data.message, response.data.success ? 'success' : 'error');
        
        if (response.data.success == 1) {
          localStorage.setItem('token', response.data.token);
         
            router.push('/dashboard'); 
        } else if (response.data.success == 2) { 
          
            router.push('/'); 
        }
      } catch (err) {
        showNotification('OTP verification failed. Please try again.', 'error');
        console.error('OTP verification error:', err);
      } finally {
        loading.value = false;
      }
    };

    const resendOTP = () => {
      showNotification('OTP resent successfully!', 'success');
      // In a real implementation, you would call an API endpoint to resend OTP
    };

    const closeModal = () => {
      verified.value = false;
    };

    const showNotification = (message, type) => {
      // In a real implementation, you would use a notification library
      // For now, we'll use a simple alert
      alert(`${type.toUpperCase()}: ${message}`);
    };

    return {
      darkMode,
      loading,
      showPassword,
      termsAgreed,
      verified,
      otpverified,
      register,
      verify,
      errors,
      toggleTheme,
      validateName,
      validateMobile,
      validatePassword,
      userregister,
      verifyotp,
      resendOTP,
      closeModal
    };
  }
}
</script>